import { Stack } from "@mui/material";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useGridDataSourceWithRange } from "@entrilia/shared/components/grid/gridHooks";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import adminApi from "../../../api/adminApi";
import type { InvestorCommunicationInfo } from "../../../api/types/investorCommunicationsTypes";
import useClientContext from "../../../context/useClientContext";
import { FilterContextProvider } from "../../common/filters/FilterContextProvider";
import FiltersPanel from "../../common/filters/FiltersPanel";
import { getInitialFilterState } from "../../common/filters/filterState";
import ChangeInvestorCommunicationStatusDialog from "./ChangeInvestorCommunicationStatusDialog";
import DeleteInvestorCommunicationDialog from "./DeleteInvestorCommunicationDialog";
import InvestorCommunicationsGrid from "./InvestorCommunicationsGrid";
import {
  filterDefinitions,
  getInvestorCommunicationsFilterPayload,
  selectOptionsResolver,
} from "./investorCommunicationsGridFilters";
import { InvestorCommunicationsPageContextProvider } from "./InvestorCommunicationsPageContext";
import InvestorCommunicationsPageHeader from "./InvestorCommunicationsPageHeader";
import type { InvestorCommunicationStatusTransition } from "./investorCommunicationStatusHelper";
import NewInvestorCommunicationDialog from "./NewInvestorCommunicationDialog";
import NewsPageNotLiveAlert from "./NewsPageNotLiveAlert";
import useIsNewsVisibleToInvestors from "./useIsNewsVisibleToInvestors";

interface StatusChangeTarget {
  communication: InvestorCommunicationInfo;
  statusTransition: InvestorCommunicationStatusTransition;
}

const InvestorCommunicationsPage = () => {
  usePageTitle("Investor Communications");

  const { clientCode, hasPermissions } = useClientContext();
  const navigate = useNavigate();

  const [isNewCommunicationDialogOpen, setIsNewCommunicationDialogOpen] = useState(false);
  const [statusChangeTarget, setStatusChangeTarget] = useState<StatusChangeTarget>();
  const [deleteTarget, setDeleteTarget] = useState<InvestorCommunicationInfo>();

  const filterId = useMemo(() => `${clientCode}_investor_communications_filter_v1`, [clientCode]);

  const [filterState, setFilterState] = useState(() => getInitialFilterState(filterId, filterDefinitions, []));

  const filterPayload = useMemo(() => getInvestorCommunicationsFilterPayload(filterState), [filterState]);

  const { gridApiRef, dataSource, totalRows, isLoading, isRefreshing, refreshDataSource } = useGridDataSourceWithRange(
    adminApi.searchInvestorCommunications,
    filterPayload
  );

  const isNewsVisibleToInvestors = useIsNewsVisibleToInvestors();

  const handleStatusUpdated = () => {
    setStatusChangeTarget(undefined);
    refreshDataSource();
  };

  const handleDeleted = () => {
    setDeleteTarget(undefined);
    refreshDataSource();
  };

  return (
    <InvestorCommunicationsPageContextProvider
      hasEditPermissions={hasPermissions(["ManageInvestorCommunications"])}
      isNewsVisibleToInvestors={isNewsVisibleToInvestors}
      onCreate={() => setIsNewCommunicationDialogOpen(true)}
      onStatusChange={(communication, statusTransition) => setStatusChangeTarget({ communication, statusTransition })}
      onDelete={setDeleteTarget}
    >
      <FilterContextProvider
        filterState={filterState}
        setFilterState={setFilterState}
        getSelectOptionsForFilter={selectOptionsResolver}
      >
        <InvestorCommunicationsPageHeader />

        <Stack spacing={2} sx={{ width: "100%", pt: 2.5, px: 3, flex: 1 }}>
          {!isNewsVisibleToInvestors && <NewsPageNotLiveAlert />}

          <FiltersPanel
            recordCount={totalRows ?? 0}
            isLoading={isLoading}
            isRefreshing={isRefreshing}
            onRefresh={refreshDataSource}
          />

          <InvestorCommunicationsGrid apiRef={gridApiRef} dataSource={dataSource} filterId={filterId} />
        </Stack>
      </FilterContextProvider>

      {isNewCommunicationDialogOpen && (
        <NewInvestorCommunicationDialog
          onClose={() => setIsNewCommunicationDialogOpen(false)}
          onCreated={(communicationId) => navigate(`${communicationId}?tab=recipients`)}
        />
      )}

      {statusChangeTarget && (
        <ChangeInvestorCommunicationStatusDialog
          investorCommunicationId={statusChangeTarget.communication.id}
          expectedEtag={statusChangeTarget.communication.etag}
          statusTransition={statusChangeTarget.statusTransition}
          onClose={() => setStatusChangeTarget(undefined)}
          onUpdated={handleStatusUpdated}
        />
      )}

      {deleteTarget && (
        <DeleteInvestorCommunicationDialog
          investorCommunicationId={deleteTarget.id}
          investorCommunicationTitle={deleteTarget.title || "Untitled"}
          onClose={() => setDeleteTarget(undefined)}
          onDeleted={handleDeleted}
        />
      )}
    </InvestorCommunicationsPageContextProvider>
  );
};

export default InvestorCommunicationsPage;
